import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X, DollarSign, CreditCard, Wallet } from 'lucide-react';
import '../utils/notificationStyles.css';
import errorNotificationService from '../services/ErrorNotificationService';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

const NotificationItem = ({ notification, onClose, onPause, onResume, isDismissing }) => {
  const [progress, setProgress] = useState(100);
  const [isPaused, setIsPaused] = useState(false);
  const progressRef = useRef(100);
  const intervalRef = useRef(null);

  // Progress bar animation with pause on hover
  useEffect(() => {
    // Reset progress when notification changes or when resuming from pause
    if (!isDismissing && !isPaused) {
      progressRef.current = 100;
      setProgress(100);
    }
    
    if (notification.duration > 0 && !isDismissing && !isPaused) {
      const interval = 50; // Update every 50ms for smooth animation
      const totalSteps = notification.duration / interval;
      const decrement = 100 / totalSteps;
      
      intervalRef.current = setInterval(() => {
        progressRef.current = Math.max(0, progressRef.current - decrement);
        setProgress(progressRef.current);
        
        if (progressRef.current <= 0) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }, interval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else if (isPaused && intervalRef.current) {
      // Pause the progress
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [notification.duration, isDismissing, isPaused, notification.id]);

  // Handle close - trigger dismiss which will handle animation
  const handleClose = () => {
    onClose(notification.id);
  };

  const getIcon = () => {
    // Payment-specific icons
    if (notification.category === 'payment') {
      if (notification.type === 'success') {
        return <div className="relative">
          <CheckCircle className="w-6 h-6 text-acctoo-green-500" />
          <DollarSign className="w-3 h-3 text-acctoo-green-600 absolute -bottom-0.5 -right-0.5 bg-white rounded-full p-0.5" />
        </div>;
      }
      return <CreditCard className="w-5 h-5 text-acctoo-blue-500" />;
    }
    
    // Default icons
    switch (notification.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-acctoo-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-acctoo-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-acctoo-orange-500" />;
      default:
        return <Info className="w-5 h-5 text-acctoo-500" />;
    }
  };

  const getBackgroundColor = () => {
    // Payment-specific styling
    if (notification.category === 'payment') {
      if (notification.type === 'success') {
        return 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-400 shadow-lg';
      }
      return 'bg-blue-50 border-blue-300';
    }
    
    switch (notification.type) {
      case 'success':
        return 'bg-acctoo-green-50 border-acctoo-green-300';
      case 'error':
        return 'bg-acctoo-red-50 border-acctoo-red-300';
      case 'warning':
        return 'bg-acctoo-orange-50 border-acctoo-orange-300';
      default:
        return 'bg-acctoo-50 border-acctoo-300';
    }
  };

  const getTextColor = () => {
    switch (notification.type) {
      case 'success':
        return 'text-acctoo-green-900';
      case 'error':
        return 'text-acctoo-red-900';
      case 'warning':
        return 'text-acctoo-orange-900';
      default:
        return 'text-acctoo-900';
    }
  };

  const isPaymentNotification = notification.category === 'payment';
  
  return (
    <div 
      onMouseEnter={() => {
        setIsPaused(true);
        onPause?.(notification.id);
      }}
      onMouseLeave={() => {
        setIsPaused(false);
        onResume?.(notification.id, notification);
      }}
      className={`relative flex items-start gap-3 p-3 sm:p-4 rounded-xl border-2 shadow-xl w-full max-w-full mb-2 sm:mb-3 transition-all duration-300 ${
        isDismissing 
          ? 'animate-slide-out-right opacity-0 scale-95' 
          : 'animate-slide-in-right opacity-100 scale-100'
      } ${getBackgroundColor()} ${isPaymentNotification && notification.type === 'success' ? 'ring-2 ring-green-200' : ''} hover:shadow-2xl hover:scale-[1.02]`}
    >
      {/* Progress Bar */}
      {notification.duration > 0 && !isDismissing && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-black bg-opacity-5 rounded-t-xl overflow-hidden">
          <div 
            className={`h-full transition-all duration-50 ${
              isPaused ? 'opacity-50' : ''
            } ${
              notification.type === 'success' ? 'bg-green-500' :
              notification.type === 'error' ? 'bg-red-500' :
              notification.type === 'warning' ? 'bg-orange-500' :
              'bg-blue-500'
            }`}
            style={{ 
              width: `${progress}%`,
              transition: isPaused ? 'none' : 'width 50ms linear'
            }}
          />
        </div>
      )}
      
      {/* Pause Indicator */}
      {isPaused && !isDismissing && (
        <div className="absolute top-2 right-12 bg-black bg-opacity-20 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Paused
        </div>
      )}
      
      <div className={`flex-shrink-0 mt-0.5 ${isPaymentNotification && notification.type === 'success' ? 'animate-pulse' : ''}`}>
        {getIcon()}
      </div>
      <div className={`flex-1 ${getTextColor()}`}>
        {notification.title && (
          <p className={`font-bold mb-1 ${isPaymentNotification ? 'text-base flex items-center gap-2' : 'text-base'}`}>
            {isPaymentNotification && notification.type === 'success' && (
              <Wallet className="w-4 h-4 text-green-600" />
            )}
            {notification.title}
          </p>
        )}
        <p className="text-sm whitespace-pre-line leading-relaxed">{notification.message}</p>
        {notification.details && (
          <div className="mt-2 pt-2 border-t border-opacity-20">
            <p className="text-xs opacity-80">{notification.details}</p>
          </div>
        )}
      </div>
      <button
        onClick={handleClose}
        className={`flex-shrink-0 ${getTextColor()} opacity-60 hover:opacity-100 transition-all hover:scale-110`}
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [dismissingIds, setDismissingIds] = useState(new Set());
  const timeoutRefs = useRef({});

  const scheduleDismiss = useCallback((id, durationMs, createdAt = Date.now()) => {
    if (durationMs <= 0) return;
    const elapsed = Date.now() - createdAt;
    const remaining = Math.max(0, durationMs - elapsed);
    if (remaining <= 0) return;
    const dismissTimeout = setTimeout(() => {
      setDismissingIds(prev => new Set([...prev, id]));
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
        setDismissingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        delete timeoutRefs.current[id];
      }, 300);
    }, remaining);
    timeoutRefs.current[id] = dismissTimeout;
  }, []);

  const show = useCallback((message, type = 'info', options = {}) => {
    const id = Date.now() + Math.random();
    const createdAt = Date.now();
    const duration = options.duration ?? 5000;
    const notification = {
      id,
      message,
      type,
      title: options.title,
      category: options.category, // 'payment', 'expense', etc.
      details: options.details, // Additional details for payment notifications
      duration,
      createdAt
    };

    setNotifications(prev => [...prev, notification]);

    // Auto-dismiss after duration with smooth animation
    if (duration > 0) {
      scheduleDismiss(id, duration, createdAt);
    }

    return id;
  }, [scheduleDismiss]);
  
  // Payment-specific notification helper
  const paymentSuccess = useCallback((message, options = {}) => {
    return show(message, 'success', { 
      ...options, 
      category: 'payment',
      duration: options.duration || 6000 
    });
  }, [show]);

  const success = useCallback((message, options = {}) => {
    return show(message, 'success', options);
  }, [show]);

  const error = useCallback((message, options = {}) => {
    return show(message, 'error', { ...options, duration: options.duration || 7000 });
  }, [show]);

  const warning = useCallback((message, options = {}) => {
    return show(message, 'warning', options);
  }, [show]);

  const info = useCallback((message, options = {}) => {
    return show(message, 'info', options);
  }, [show]);

  const pauseNotification = useCallback((id) => {
    if (timeoutRefs.current[id]) {
      clearTimeout(timeoutRefs.current[id]);
      delete timeoutRefs.current[id];
    }
  }, []);

  const resumeNotification = useCallback((id, notification) => {
    if (!notification || notification.duration <= 0) return;
    const remaining = Math.max(0, notification.duration - (Date.now() - (notification.createdAt || 0)));
    if (remaining > 0) {
      scheduleDismiss(id, remaining, Date.now()); // remaining ms from now
    }
  }, [scheduleDismiss]);

  const dismiss = useCallback((id) => {
    // Clear timeout if exists
    if (timeoutRefs.current[id]) {
      clearTimeout(timeoutRefs.current[id]);
      delete timeoutRefs.current[id];
    }
    
    // Mark as dismissing to trigger exit animation
    setDismissingIds(prev => new Set([...prev, id]));
    
    // Actually remove after animation completes
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
      setDismissingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, 300); // Match slide-out animation duration
  }, []);

  const dismissAll = useCallback(() => {
    // Clear all timeouts
    Object.values(timeoutRefs.current).forEach(timeout => clearTimeout(timeout));
    timeoutRefs.current = {};
    
    // Mark all as dismissing
    const allIds = new Set(notifications.map(n => n.id));
    setDismissingIds(allIds);
    
    // Remove all after animation
    setTimeout(() => {
      setNotifications([]);
      setDismissingIds(new Set());
    }, 300);
  }, [notifications]);

  // Initialize ErrorNotificationService when notification methods are ready
  useEffect(() => {
    if (show && success && error && warning && info && paymentSuccess) {
      errorNotificationService.initialize({
        show,
        success,
        error,
        warning,
        info,
        paymentSuccess
      });
    }
  }, [show, success, error, warning, info, paymentSuccess]);

  return (
    <NotificationContext.Provider value={{ show, success, error, warning, info, paymentSuccess, dismiss, dismissAll }}>
      {children}
      
      {/* Notification Container — above modals (e.g. invoice preview z-index 10050) so success/error toasts stay readable */}
      {notifications.length > 0 && (
        <div className="notification-stack">
          {/* Dismiss All Button - Show when 2+ notifications */}
          {notifications.length > 1 && (
            <div className="mb-3 flex justify-end">
              <button
                onClick={dismissAll}
                className="text-xs font-bold px-4 py-2 bg-gradient-to-r from-acctoo-700 to-acctoo-900 hover:from-acctoo-800 hover:to-acctoo-950 text-white rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center gap-2 animate-fade-in"
              >
                <X className="w-3.5 h-3.5" />
                Dismiss All ({notifications.length})
              </button>
            </div>
          )}
          
          {/* Notifications Stack */}
          {notifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onClose={dismiss}
              onPause={pauseNotification}
              onResume={resumeNotification}
              isDismissing={dismissingIds.has(notification.id)}
            />
          ))}
        </div>
      )}
      
      {/* Debug: Show notification count */}
      {process.env.NODE_ENV === 'development' && notifications.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-black text-white p-2 rounded text-xs z-[12001]">
          Notifications: {notifications.length}
        </div>
      )}
    </NotificationContext.Provider>
  );
};

